import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                <p className="font-medium text-gray-800">{`${label}, 2025`}</p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color }} className="text-sm">
                        {`${entry.dataKey === 'sales' ? 'Net Sales' : 'Discounts'}: $${entry.value.toLocaleString()}`}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

// Loader component
const Loader = () => (
    <div className="flex justify-center items-center h-full min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-[#E57320]  border-solid"></div>
    </div>
);

export default function SalesDiscountDailyChart() {
    const [dailyComparisonData, setDailyComparisonData] = useState([]);
    const [range, setRange] = useState('thisWeek');
    const [loading, setLoading] = useState(true);
    const restaurantId = sessionStorage?.getItem("store_id") || "Null";

    useEffect(() => {
        // Fetch data from API
        setLoading(true);
        axios.get(`/server/dashboard-functions/daily-comparison/${restaurantId}?filter=${range}`)
            .then(response => {
                if (response.data.success) {
                    const formattedData = response.data.data.map(item => ({
                        date: item.date,
                        sales: item.sales,
                        discounts: item.discounts
                    }));
                    setDailyComparisonData(formattedData);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error("There was an error fetching the data!", error);
                setLoading(false);
            });
    }, [range, restaurantId]);

    if (loading) return <Loader />;

    return (
        <div className="w-full max-w-4xl mx-auto p-4">
            <div className='flex justify-between items-center mb-4'>
                <h2 className="text-2xl text-gray-800">
                    Daily Sales vs. Discounts
                </h2>

         <div className="relative inline-block w-[180px]">
  <select
    value={range}
    onChange={(e) => setRange(e.target.value)}
    className="appearance-none border border-gray-300 px-4 py-2 rounded-lg text-gray-800 bg-white 
    shadow-sm hover:shadow-md focus:ring-2 focus:ring-[#EF7320] focus:border-[#EF7320] 
    cursor-pointer transition-all duration-200 outline-none pr-8 w-full"
  >
    <option value="thisWeek" className="text-gray-800 hover:text-[#EF7320]">
      This Week
    </option>
    <option value="lastWeek" className="text-gray-800 hover:text-[#EF7320]">
      Last Week
    </option>
    <option value="last30Days" className="text-gray-800 hover:text-[#EF7320]">
      Last 30 Days
    </option>
    <option value="thisMonth" className="text-gray-800 hover:text-[#EF7320]">
      This Month
    </option>
  </select>

  {/* Custom dropdown arrow */}
  <svg
    className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
</div>




            </div>

            <div className="">
                <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart
                        data={dailyComparisonData}
                        margin={{
                            top: 20,
                            right: 80,
                            bottom: 20,
                            left: 20,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            interval="preserveStartEnd"
                            tick={{ fontSize: 12 }}
                            label={{ value: "Date", position: "insideBottom", offset: 15, fontSize: 14, fill: "#555" }}
                        />
                        <YAxis
                            yAxisId="sales"
                            orientation="left"
                            tick={{ fontSize: 12 }}
                            axisLine={{ stroke: '#666' }}
                            label={{
                                value: 'Net Sales ($)',
                                angle: -90,
                                position: 'insideLeft',
                                style: { textAnchor: 'middle' }
                            }}
                        />
                        <YAxis
                            yAxisId="discounts"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            axisLine={{ stroke: '#666' }}
                            label={{
                                value: 'Discounts ($)',
                                angle: 90,
                                position: 'insideRight',
                                style: { textAnchor: 'middle' }
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{ paddingTop: '25px' }}
                            iconType="rect"
                        />
                        <Bar
                            yAxisId="sales"
                            dataKey="sales"
                            fill="#EF7320"
                            name="Net Sales"
                            radius={[2, 2, 0, 0]}
                        />
                        <Line
                            yAxisId="discounts"
                            type="monotone"
                            dataKey="discounts"
                            stroke="#9369A9"
                            strokeWidth={3}
                            name="Discounts"
                            dot={{ fill: '#F2F2F2', strokeWidth: 2, r: 4 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}